import React, { useState, useMemo } from 'react';
import { useGeneration } from '../../contexts/GenerationContext';
import { GenerationJobItem } from './GenerationJobItem';
import { Button } from '../ui';

export interface GenerationQueueProps {
  excludeActiveJob?: boolean;
  className?: string;
}

const ITEMS_PER_PAGE = 5;

export const GenerationQueue: React.FC<GenerationQueueProps> = ({
  excludeActiveJob = false,
  className = '',
}) => {
  const { jobs, activeJobId, removeJob, cancelJob, isLoadingJobs } = useGeneration();
  const [inProgressPage, setInProgressPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);

  // Convert jobs Map to array for stable dependency tracking
  const jobsArray = useMemo(() => Array.from(jobs.values()), [jobs, jobs.size]);

  // Separate jobs into in-progress and previously generated
  const { inProgressJobs, completedJobs } = useMemo(() => {
    const allJobs = jobsArray.filter(job => {
      // Optionally exclude the active job (when showing queue alongside main generating view)
      if (excludeActiveJob && job.id === activeJobId) {
        return false;
      }
      return true;
    });

    const inProgress = allJobs
      .filter(job => job.status === 'generating')
      .sort((a, b) => b.startedAt - a.startedAt);

    const completed = allJobs
      .filter(job => job.status === 'complete' || job.status === 'failed')
      .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));

    return { inProgressJobs: inProgress, completedJobs: completed };
  }, [jobsArray, excludeActiveJob, activeJobId]);

  // Pagination logic for in-progress jobs
  const inProgressTotalPages = Math.ceil(inProgressJobs.length / ITEMS_PER_PAGE);
  const paginatedInProgress = inProgressJobs.slice(
    (inProgressPage - 1) * ITEMS_PER_PAGE,
    inProgressPage * ITEMS_PER_PAGE
  );

  // Pagination logic for completed jobs
  const completedTotalPages = Math.ceil(completedJobs.length / ITEMS_PER_PAGE);
  const paginatedCompleted = completedJobs.slice(
    (completedPage - 1) * ITEMS_PER_PAGE,
    completedPage * ITEMS_PER_PAGE
  );

  const renderPagination = (
    currentPage: number,
    totalPages: number,
    setPage: (page: number) => void
  ) => {
    if (totalPages <= 1) return null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-3)',
        }}
      >
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-muted)',
          }}
        >
          Page {currentPage} of {totalPages}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div>
    );
  };

  const renderSection = (
    title: string,
    jobs: typeof inProgressJobs,
    totalCount: number,
    currentPage: number,
    totalPages: number,
    setPage: (page: number) => void,
    emptyMessage: string
  ) => {
    return (
      <div
        style={{
          marginBottom: 'var(--space-6)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              margin: 0,
            }}
          >
            {title}
          </h3>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-muted)',
            }}
          >
            {totalCount} {totalCount === 1 ? 'item' : 'items'}
          </span>
        </div>

        {totalCount === 0 ? (
          <div
            style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--bg-sunk)',
              border: 'var(--border-thin)',
              textAlign: 'center',
              color: 'var(--color-muted)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {emptyMessage}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {jobs.map(job => (
              <GenerationJobItem
                key={job.id}
                job={job}
                onRemove={removeJob}
                onCancel={cancelJob}
              />
            ))}
          </div>
        )}

        {renderPagination(currentPage, totalPages, setPage)}
      </div>
    );
  };

  // Show loader while loading
  if (isLoadingJobs) {
    return (
      <div
        className={className}
        style={{
          marginTop: 'var(--space-8)',
          padding: 'var(--space-6)',
          backgroundColor: 'var(--color-bg)',
          border: 'var(--border-thin)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-8)',
            color: 'var(--color-muted)',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              animation: 'spin 1s linear infinite',
            }}
          >
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
          </svg>
          <span>Loading generations...</span>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        marginTop: 'var(--space-8)',
        padding: 'var(--space-6)',
        backgroundColor: 'var(--color-bg)',
        border: 'var(--border-thin)',
      }}
    >
      {renderSection(
        'In Progress',
        paginatedInProgress,
        inProgressJobs.length,
        inProgressPage,
        inProgressTotalPages,
        setInProgressPage,
        'No generations in progress. Start a new generation to see it here.'
      )}

      {renderSection(
        'Previously Generated',
        paginatedCompleted,
        completedJobs.length,
        completedPage,
        completedTotalPages,
        setCompletedPage,
        'No completed generations yet. Your finished websites will appear here.'
      )}
    </div>
  );
};

export default GenerationQueue;
